import React, { useEffect } from "react";
import moment from "moment";
import styled from "styled-components";
import { unparse as convertToCSV } from "papaparse/papaparse.min";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  TextInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  FilterButton,
  ExportButton,
  CreateButton,
  TopToolbar,
  FunctionField,
  useRefresh,
} from "react-admin";

import { ListPagination, downloadCsv } from "../../utils";
import processStatusChoice from "../../constants/choices/processStatusChoice";
import SendHistoryActionsMenu from "./LogisticSendHistoryActionsMenu/LogisticSendHistoryActionsMenu";

const StyledDatagrid = styled(Datagrid)`
  .RaDatagrid-headerCell {
    text-transform: none;
    color: #4a4a4a;
    font-weight: 500;
    white-space: nowrap;
  }

  .RaDatagrid-headerCell.column-successCount,
  .RaDatagrid-rowCell.column-successCount {
    text-align: center;
    max-width: 160px;
    width: 160px;
  }

  .RaDatagrid-rowCell {
    color: #000;
    text-align: left;
  }

  .RaDatagrid-tableWrapper {
    overflow-x: auto;
  }
`;

const statusColorMap = {
  ENVIADO: {
    borderColor: "#2E7D32",
    color: "#2E7D32",
    backgroundColor: "transparent",
  },
  "ERRO NO ENVIO": {
    borderColor: "#C62828",
    color: "#C62828",
    backgroundColor: "transparent",
  },
  "EM PROCESSAMENTO": {
    borderColor: "#EDB200",
    color: "#A66A00",
    backgroundColor: "transparent",
  },
};

const getStatusStyle = (status) =>
  statusColorMap[status] || {
    borderColor: "#9E9E9E",
    color: "#616161",
    backgroundColor: "transparent",
  };

const exporter = (data) => {
  const dataForExport = data.map((item) => ({
    "Número do ciclo": item?.cycleId ?? "",
    Laboratório: item?.lab?.name ?? "",
    "Data de criação do ciclo": item?.cycleCreatedAt
      ? moment(item.cycleCreatedAt).format("DD/MM/YYYY HH:mm")
      : "",
    "Data de atualização do ciclo": item?.cycleUpdatedAt
      ? moment(item.cycleUpdatedAt).format("DD/MM/YYYY HH:mm")
      : "",
    "Data de envio": item?.sentAt
      ? moment(item.sentAt).format("DD/MM/YYYY HH:mm")
      : "",
    "Total de pedidos": item?.successCount ?? "",
    "Status de envio": item?.processStatus?.name ?? "",
  }));

  const csv = convertToCSV(
    {
      data: dataForExport,
      fields: [
        "Número do ciclo",
        "Laboratório",
        "Data de criação do ciclo",
        "Data de atualização do ciclo",
        "Data de envio",
        "Total de pedidos",
        "Status de envio",
        
      ],
    },
    { delimiter: ";" }
  );

  const filename = `Extracao_Envios_Pedidos_${moment().format('YYYYMMDD')}`;
  downloadCsv(csv, filename);
};

const cycleLogisticsSendHistoryFilters = [
  <TextInput key="cycleId" label="Ciclo" source="cycleId" alwaysOn />,
  <ReferenceInput
    key="labId"
    sort={{ field: "name", order: "ASC" }}
    perPage={999}
    resource="lab"
    label="Laboratório"
    source="labId"
    reference="labs"
  >
    <SelectInput label="Laboratório" optionText="name" optionValue="id" />
  </ReferenceInput>,
  <SelectInput
    key="processStatusId"
    label="Status do envio"
    source="processStatusId"
    choices={processStatusChoice}
    optionText="name"
    optionValue="id"
  />,
  <DateInput key="cycleCreatedAt.gte" label="Data criação do ciclo de" source="cycleCreatedAt.gte" />,
  <DateInput key="cycleCreatedAt.lte" label="Data criação do ciclo até" source="cycleCreatedAt.lte" />,
  <DateInput key="cycleUpdatedAt.gte" label="Data atualização do ciclo de" source="cycleUpdatedAt.gte" />,
  <DateInput key="cycleUpdatedAt.lte" label="Data atualização do ciclo até" source="cycleUpdatedAt.lte" />,
  <DateInput key="sentAt.gte" label="Data envio ao parceiro de" source="sentAt.gte" />,
  <DateInput key="sentAt.lte" label="Data envio ao parceiro até" source="sentAt.lte" />,
];

const ListActions = (props) => (
  <TopToolbar>
    <FilterButton filters={cycleLogisticsSendHistoryFilters} {...props} />
    <CreateButton
      label="NOVO ENVIO"
      style={{ color: "#1976D2", borderColor: "#1976D2" }}
    />
    <ExportButton disabled={false} {...props} />
  </TopToolbar>
);

const CycleLogisticsSendHistoryList = (props) => {
  const refresh = useRefresh();

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <List
      {...props}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
      pagination={<ListPagination />}
      actions={<ListActions />}
      empty={false}
      exporter={exporter}
      bulkActionButtons={<div />}
      filters={cycleLogisticsSendHistoryFilters}
      title="Lista dos Envios"
    >
      <StyledDatagrid>
        <TextField source="cycleId" label="Ciclo" />
        <TextField source="lab.name" label="Laboratório" emptyText="-" />
        <DateField
          source="cycleCreatedAt"
          label="Data de criação do ciclo"
          locales="pt-BR"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}        emptyText="-"
        />
        <DateField
          source="cycleUpdatedAt"
          label="Data de atualização do ciclo"
          locales="pt-BR"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}        emptyText="-"
        />
        <DateField
          source="sentAt"
          label="Data de envio"
          locales="pt-BR"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          emptyText="-"
        />
        <NumberField source="successCount" label="Total de pedidos" sortable={false} />
        <FunctionField
          label="Status de envio"
          source="processStatus.name"
          sortBy="processStatus.name"
          sortable={false}
          render={(record) => {
            const normalizedStatus = (record?.processStatus?.name || "")
              .toUpperCase()
              .trim();
            const statusStyle = getStatusStyle(normalizedStatus);

            return (
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  border: `1px solid ${statusStyle.borderColor}`,
                  color: statusStyle.color,
                  backgroundColor: statusStyle.backgroundColor,
                  fontSize: "11px",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  width: "max-content",
                }}
              >
                {normalizedStatus || "-"}
              </span>
            );
          }}
        />
        <SendHistoryActionsMenu />
      </StyledDatagrid>
    </List>
  );
};

export default CycleLogisticsSendHistoryList;
